import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  UserCheck, Clock, CheckCircle2, XCircle, ShieldCheck,
  Building2, Phone, Target, Thermometer, User, ChevronRight,
} from "lucide-react";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDialog } from "@/context/DialogProvider";
import { useSite } from "@/context/SiteContext";

/**
 * Tenant-side Visitor Registration hub.
 *
 * Two tabs: "Pending sign-off" (submissions waiting for staff attestation)
 * and "History" (fully signed-off entries). Staff open a submission, verify
 * the visitor's answers, type their name + tick the attestation checkbox,
 * and the record is frozen forever.
 */
export default function VisitorRegistration() {
  const { activeSiteId } = useSite();
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/visitor-registrations", { params: { status: tab === "pending" ? "pending" : "signed_off" } });
      setRows(data.registrations || []);
    } catch (e) {
      // silent — show empty state
      setRows([]);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [tab, activeSiteId]);

  return (
    <div className="vb-fade-in" data-testid="visitor-registration-page">
      <div className="flex items-center gap-3 mb-2">
        <UserCheck size={26} style={{ color: "var(--vb-accent)" }} />
        <h1 className="vb-heading text-3xl" style={{ color: "var(--vb-text-2)" }}>Visitor Registration</h1>
      </div>
      <p className="opacity-70 mb-4 max-w-3xl">
        Visitor entries submitted via the site QR code land here. Verify each visitor&apos;s
        answers, then sign off to accept responsibility for their access to the premises.
        Once signed off, the entry is locked into the audit trail.
      </p>

      <div className="flex gap-2 mb-3" role="tablist">
        <TabBtn active={tab === "pending"} onClick={() => setTab("pending")} testid="tab-pending">
          <Clock size={13} /> Pending sign-off
          <PendingBadge count={tab === "pending" ? rows.length : null} />
        </TabBtn>
        <TabBtn active={tab === "history"} onClick={() => setTab("history")} testid="tab-history">
          <ShieldCheck size={13} /> History
        </TabBtn>
      </div>

      {loading ? (
        <div className="vb-card p-6 opacity-60" data-testid="vr-loading">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="vb-card p-6 opacity-70" data-testid="vr-empty">
          {tab === "pending"
            ? "No visitors waiting for sign-off."
            : "No signed-off visitor entries yet."}
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <VisitorRow key={r.id} row={r} onOpen={() => setSelected(r)} />
          ))}
        </div>
      )}

      {selected && (
        <VisitorDetailModal registration={selected}
          onClose={() => setSelected(null)}
          onSaved={() => { setSelected(null); load(); }} />
      )}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="vb-btn sm" style={{
        background: active ? "var(--vb-accent)" : "transparent",
        color: active ? "white" : "var(--vb-text-2)",
        border: `1px solid ${active ? "transparent" : "var(--vb-border)"}`,
      }}>
      {children}
    </button>
  );
}

function PendingBadge({ count }) {
  if (count == null || count === 0) return null;
  return (
    <span style={{
      marginLeft: 4, background: "rgba(255,255,255,0.25)", padding: "1px 6px",
      borderRadius: 10, fontSize: 11, fontWeight: 700,
    }}>{count}</span>
  );
}

function VisitorRow({ row, onOpen }) {
  const submitted = new Date(row.submitted_at).toLocaleString();
  const flagged = (row.answers || []).some((a) => a.answer === "yes");
  return (
    <button onClick={onOpen} data-testid={`vr-row-${row.id}`}
      className="vb-card p-3 w-full text-left flex items-center gap-3 hover:shadow-md transition-shadow"
      style={{ cursor: "pointer" }}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <strong className="text-[14px]" style={{ color: "var(--vb-text-2)" }}>{row.name}</strong>
          <span className="text-[12px] opacity-70">· {row.company}</span>
          {flagged && (
            <span className="vb-pill warn text-[10px]">
              <XCircle size={10} /> Yes flagged
            </span>
          )}
          {row.status === "signed_off" && (
            <span className="vb-pill ok text-[10px]">
              <CheckCircle2 size={10} /> Signed off
            </span>
          )}
        </div>
        <div className="text-[11px] opacity-60 mt-0.5">
          {row.site_name} · {row.purpose} · {submitted}
          {row.status === "signed_off" && row.staff_name && (
            <span> · by {row.staff_name}</span>
          )}
        </div>
      </div>
      <ChevronRight size={16} className="opacity-40" />
    </button>
  );
}

function VisitorDetailModal({ registration, onClose, onSaved }) {
  const { user } = useAuth();
  const { notify } = useDialog();
  const canSignOff = ["owner", "manager", "supervisor"].includes(user?.role);
  const isPending = registration.status === "pending";
  const [staffName, setStaffName] = useState(user?.name || user?.email || "");
  const [attest, setAttest] = useState(false);
  const [busy, setBusy] = useState(false);
  const flaggedYes = (registration.answers || []).some((a) => a.answer === "yes");

  const signOff = async () => {
    setBusy(true);
    try {
      await api.post(`/visitor-registrations/${registration.id}/sign-off`, {
        staff_name: staffName.trim(),
        attest,
      });
      await notify({ title: "Signed off", message: `${registration.name}'s entry is locked into the audit trail.`, variant: "ok" });
      onSaved();
    } catch (e) {
      await notify({ title: "Couldn't sign off", message: formatErr(e.response?.data?.detail), variant: "err" });
    } finally { setBusy(false); }
  };

  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.4)", zIndex: 12000 }}
      onClick={onClose} data-testid="vr-detail-modal">
      <div className="vb-card p-5"
        style={{ maxWidth: 640, width: "100%", maxHeight: "90vh", overflowY: "auto" }}
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-2 mb-3">
          <div>
            <h2 className="vb-heading text-xl m-0" style={{ color: "var(--vb-text-2)" }}>
              {registration.name}
            </h2>
            <div className="text-[12px] opacity-70">
              {registration.company} · {registration.site_name} · {registration.form_ref}
            </div>
          </div>
          {registration.status === "signed_off" && (
            <span className="vb-pill ok text-[10px]">
              <CheckCircle2 size={10} /> Signed off
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3 text-[13px] mb-3">
          <InfoCell icon={Building2} label="Company">{registration.company}</InfoCell>
          <InfoCell icon={Phone} label="Mobile">{registration.mobile}</InfoCell>
          <InfoCell icon={Target} label="Purpose">{registration.purpose}</InfoCell>
          <InfoCell icon={Thermometer} label="Temperature">{registration.temperature || "—"}</InfoCell>
          <InfoCell icon={Clock} label="Submitted">
            {new Date(registration.submitted_at).toLocaleString()}
          </InfoCell>
          {registration.staff_name && (
            <InfoCell icon={User} label="Signed off by">
              {registration.staff_name} · {new Date(registration.signed_off_at).toLocaleString()}
            </InfoCell>
          )}
        </div>

        {flaggedYes && isPending && (
          <div className="mb-3 p-3 rounded" style={{ background: "rgba(239,68,68,0.10)", border: "1px solid rgba(239,68,68,0.4)" }}>
            <strong style={{ color: "#991b1b" }}>Warning:</strong>
            <span className="text-[12px] ml-1">
              This visitor answered <strong>Yes</strong> to one or more health-screening questions.
              Consider preventing entry.
            </span>
          </div>
        )}

        <div className="mb-2 text-[12px] font-semibold opacity-70">Health screening</div>
        <div className="space-y-1.5 mb-4">
          {(registration.answers || []).map((a) => (
            <div key={a.item_key} className="p-2 rounded text-[12px]"
              style={{ background: a.answer === "yes" ? "rgba(239,68,68,0.08)" : "var(--vb-bg-2,#faf6f1)" }}
              data-testid={`vr-ans-${a.item_key}`}>
              <div className="font-medium">{a.label}</div>
              {a.label_zh && <div className="opacity-60 text-[11px]">{a.label_zh}</div>}
              <div className="mt-1 flex items-center gap-2">
                <span className={`vb-pill ${a.answer === "yes" ? "err" : "ok"} text-[10px]`}>
                  {a.answer === "yes"
                    ? <><XCircle size={10} /> Yes 是</>
                    : <><CheckCircle2 size={10} /> No 否</>}
                </span>
                {a.reason && (
                  <span className="opacity-80 text-[11px]"><em>{a.reason}</em></span>
                )}
              </div>
            </div>
          ))}
        </div>

        {isPending && canSignOff && (
          <div className="p-3 rounded" style={{ background: "var(--vb-bg-2,#faf6f1)", border: "1px solid var(--vb-border)" }}>
            <div className="text-[13px] font-semibold mb-2">Staff sign-off</div>
            <label className="block text-[11px] opacity-70 mb-1">Your full name</label>
            <input className="vb-input" value={staffName} onChange={(e) => setStaffName(e.target.value)}
              data-testid="vr-signoff-name" placeholder="Type your name to sign" />
            <label className="flex items-start gap-2 mt-2 text-[12px]" data-testid="vr-signoff-attest">
              <input type="checkbox" checked={attest} onChange={(e) => setAttest(e.target.checked)} className="mt-1" />
              <span>
                I have verified this visitor&apos;s answers and confirm they are permitted
                to enter the premises. I take accountability for their visit.
              </span>
            </label>
            <div className="flex justify-end gap-2 mt-3">
              <button className="vb-btn ghost sm" onClick={onClose}>Cancel</button>
              <button className="vb-btn sm" onClick={signOff}
                disabled={busy || !attest || !staffName.trim()}
                data-testid="vr-signoff-submit">
                {busy ? "Signing…" : "Confirm & sign off"}
              </button>
            </div>
          </div>
        )}

        {(!isPending || !canSignOff) && (
          <div className="flex justify-end mt-3">
            <button className="vb-btn sm ghost" onClick={onClose}>Close</button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

function InfoCell({ icon: Icon, label, children }) {
  return (
    <div>
      <div className="flex items-center gap-1 text-[11px] opacity-60 uppercase tracking-wider mb-0.5">
        <Icon size={11} /> {label}
      </div>
      <div>{children}</div>
    </div>
  );
}
